import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CentralFileExchangeService } from '@shared/services/central-file-exchange/central-file-exchange.service';
import { SnackbarService } from '@shared/services/notification/snackbar.service';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import {
  DownloadData,
  DownloadProgress,
  DownloadProgressComponent,
} from '@widgets/download-progress/download-progress.component';
import { BehaviorSubject, Subject, concatMap, filter, interval, take, takeUntil } from 'rxjs';
import { ExportStatus, ZipExportState } from '../data/receipts.model';
import { DownloadState, ReceiptDownloadStateService } from './receipt-download-state.service';
import { ReceiptsService } from './receipts.service';

@Injectable({
  providedIn: 'root',
})
export class ReceiptDownloadMonitorService {
  private readonly downloadStateService = inject(ReceiptDownloadStateService);
  private readonly receiptsService = inject(ReceiptsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly topNotificationService = inject(TopNotificationService);
  private readonly cfeService = inject(CentralFileExchangeService);

  private readonly unsubscribe$ = new Subject<void>();

  startMonitoring(): void {
    this.downloadStateService.getActiveDownload().subscribe(download => {
      if (download) {
        this.monitorDownload(download);
      }
    });
  }

  stopFetching(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.downloadStateService.clearDownload();
  }

  private monitorDownload(download: DownloadState): void {
    const downloadProgress$ = new BehaviorSubject<DownloadProgress>({
      progress: download.progress || 0,
      progressText: this.translate.instant('RECEIPTS.DOWNLOAD_IN_PROGRESS'),
      downloadId: download.downloadId,
      fileName: download.fileName,
      downloadButtonText: this.translate.instant('RECEIPTS.DOWNLOAD_ZIP'),
      finished: false,
      failed: false,
    });

    const downloadData$ = new Subject<DownloadData>();
    downloadData$
      .pipe(
        filter(data => !!data),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(data => {
        this.cfeService.downloadFileFromCFE(
          data.downloadId,
          data.fileName,
          this.unsubscribe$,
          () => {
            this.downloadStateService.clearDownload();
          },
          error => {
            this.topNotificationService.showErrorInTopNotification(error);
            this.downloadStateService.clearDownload();
          }
        );
      });

    this.snackbarService.openDownloadProgressSnackbar(
      DownloadProgressComponent,
      downloadProgress$,
      downloadData$
    );

    const intervalSub = interval(2000)
      .pipe(
        concatMap(() =>
          this.receiptsService
            .getZipExportStatus(download.partnerId, download.receiptType)
            .pipe(take(1))
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: resp => {
          const progressData = this.mapToProgressSubject(resp);
          downloadProgress$.next(progressData);

          if (resp.downloadId) {
            this.downloadStateService.updateDownload(download.partnerId, {
              partnerId: download.partnerId,
              downloadId: resp.downloadId,
              fileName: resp.filename,
              receiptType: download.receiptType,
              progress: progressData.progress,
              startTime: download.startTime,
            });
          }

          if (resp.state === ZipExportState.FINISHED || resp.state === ZipExportState.FAILED) {
            this.downloadStateService.clearDownload();
            intervalSub.unsubscribe();
          }
        },
        error: error => {
          this.topNotificationService.showErrorInTopNotification(error);
          this.downloadStateService.clearDownload();
          intervalSub.unsubscribe();
        },
      });
  }

  private mapToProgressSubject(status: ExportStatus): DownloadProgress {
    return {
      progress:
        status.countCurrent && status.countAll
          ? Math.round((status.countCurrent / status.countAll) * 100)
          : 0,
      progressText: this.translate.instant('RECEIPTS.DOWNLOAD_IN_PROGRESS'),
      downloadId: status.downloadId,
      fileName: status.filename,
      downloadButtonText: this.translate.instant('RECEIPTS.DOWNLOAD_ZIP'),
      finished: status.state === ZipExportState.FINISHED || status.state === ZipExportState.FAILED,
      failed: status.state === ZipExportState.FAILED,
    };
  }
}
